import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../cn"
import { IconSlot } from "../Button/IconSlot"
import { Tooltip } from "../Tooltip/Tooltip"

const alertVariants = cva(
  [
    "relative flex gap-3 rounded-lg px-4 py-3 text-sm",
    "transition-colors duration-150",
    // The focus ring takes the variant's on-surface text color, which is held
    // to contrast against the status surface; the app-wide ring token is not.
    "[--coval-focus-ring:currentColor]",
  ],
  {
    variants: {
      variant: {
        info: "bg-coval-info-surface text-coval-info-on-surface",
        success: "bg-coval-success-surface text-coval-success-on-surface",
        warning: "bg-coval-warning-surface text-coval-warning-on-surface",
        danger: "bg-coval-danger-surface text-coval-danger-on-surface",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
)

export interface AlertProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  icon?: React.ReactNode
  onDismiss?: () => void
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, icon, onDismiss, children, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {icon && <IconSlot className="shrink-0 [&>svg]:size-4">{icon}</IconSlot>}
      <div className="flex-1">{children}</div>
      {onDismiss && (
        <Tooltip content="Dismiss">
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="flex size-5 shrink-0 items-center justify-center self-center rounded-full opacity-70 hover:opacity-100 focus-visible:opacity-100"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </Tooltip>
      )}
    </div>
  ),
)

Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("font-semibold leading-tight", className)}
    {...props}
  >
    {children}
  </h5>
))

AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("opacity-90", className)} {...props} />
))

AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription, alertVariants }
